import { Component, EventEmitter, OnDestroy, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { LOG_CATEGORY, LOG_SUB_TYPE, LOG_TYPE, createAuditLogKey } from "@app/audit-log/services/audit-log.constants";
import { AuditLogService } from "@app/audit-log/services/audit-log.service";
import { AuthUser } from "@app/auth/model/auth-user";
import { Entity } from "@app/entity/model/entity";
import { EntityService } from "@app/entity/services/entity.service";
import { RouterGo } from "@app/root-store/actions";
import { emailValid, firstnameEmpty, humanNameValid, lastnameEmpty } from "@app/shared/validators";
import { AppErrorStateMatcher } from "@app/shared/validators/app-error-state-matcher.service";
import { User, UserHelper } from "@app/user/model/user";
import { ROLE_HIERARCHY, ROLE_NAMES, UserRole } from "@app/user/model/user-role";
import { UserManagementService } from "@app/user/service/user-management.service";
import { Observable, Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, filter, map, switchMap, takeUntil, tap, withLatestFrom } from "rxjs/operators";
import { UserActions } from "../../store/actions";

@Component({
    selector: "app-user-add",
    templateUrl: "./user-add.component.html",
    styleUrls: ["./user-add.component.scss"],
})
export class UserAddComponent implements OnInit, OnDestroy {
    isModal = false;
    userRoles$: Observable<UserRole[]>;
    entities$: Observable<Entity[]>;
    userForm: FormGroup = this.formBuilder.group({
        firstName: ["", [firstnameEmpty, humanNameValid]],
        lastName: ["", [lastnameEmpty, humanNameValid]],
        email: ["", emailValid],
        entityId: [{ value: "", disabled: true }, Validators.required],
        roleId: ["", Validators.required],
    });
    submitAttempted = false;
    entityId: number;
    userExists: boolean;

    @Output()
    emitter = new EventEmitter<UserActions.UserActions | RouterGo>();
    public trackById: (item: any) => number | string;

    private unsubscribe$ = new Subject();

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly userService: UserManagementService,
        private readonly entityService: EntityService,
        private readonly auditLogService: AuditLogService,
        public errorMatcher: AppErrorStateMatcher
    ) {}

    ngOnInit() {
        // TODO: Fix this reference
        if (this.emitter.observers.length) {
            this.isModal = true;
        }
        this.trackById = (entity: Entity) => this.userService.trackById(entity);
        this.entities$ = this.entityService.selectCurrentEntity$().pipe(
            filter(entity => !!entity),
            tap(entity => {
                this.entityId = entity.id;
                this.userForm.patchValue({ entityId: entity.id });
            }),
            map(entity => [entity])
        );
        this.userRoles$ = this.entityService.selectCurrentEntity$().pipe(
            switchMap(entity => this.userService.dispatchAndSelectUserRoles()),
            withLatestFrom(this.userService.getAuthUser()),
            map(([roles, user]) => this.filterUserRoles(roles, user))
        );
        this.userService.subscribeToEmitter(this.emitter);

        this.watchFormEmailValueChange();
    }

    /**
     * watch form value change
     */
    private watchFormEmailValueChange() {
        this.userForm.controls.email.valueChanges
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                takeUntil(this.unsubscribe$),
                tap((value: string) => this.userForm.controls.email.setValue(value.toLowerCase())),
                switchMap(value => this.userService.validateIfStaffEmailExisted$(value)),
                tap((isExisted: boolean) => {
                    this.userForm.controls.email.setAsyncValidators(this.userService.emailExistsControl(isExisted));
                    this.userForm.controls.email.updateValueAndValidity();
                })
            )
            .subscribe();
    }

    get fields() {
        return this.userForm.controls;
    }

    saveUser(user: User = {} as User) {
        const formValue = this.userForm.getRawValue();
        const logData = {
            auditLogName: createAuditLogKey(LOG_CATEGORY.COMPANY, LOG_TYPE.STAFF, LOG_SUB_TYPE.ADD),
            entityId: this.entityId,
            data: UserHelper.getLogString(formValue),
        };

        this.submitAttempted = true;
        this.userService.upsertUser(this.userForm, this.emitter, user, logData);
    }

    cancel() {
        this.emitter.emit(this.userService.cancelAction(this.entityId));
    }

    /**
     * Remove options that the user is not allowed to select
     *
     * @param roles
     * @param user
     */
    private filterUserRoles(roles: UserRole[], user: AuthUser): UserRole[] {
        const allowedRoles = ROLE_HIERARCHY[user.role.name];
        roles = roles.filter(r => r.name !== ROLE_NAMES.CLARE_ADMIN);
        if (!allowedRoles) {
            return roles;
        }

        return roles.filter(r => allowedRoles.includes(r.name));
    }

    ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }
}
